// components/profile/EventHistory.jsx
import React from "react";
import {
  CalendarDays,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import ShimmerLoader from "../common/ShimmerLoader";
import Card from "../common/Card";
import Button from "../common/Button";

const EventHistory = () => {
  const { user } = useAuth();
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <section className="space-y-10">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Event History</h2>
          <p className="text-sm text-gray-500">Loading your events...</p>
        </div>
        <ShimmerLoader count={4} />
      </section>
    );
  }

  // Example event data (can later be fetched dynamically)
  const events = user?.events || [
    {
      id: 1,
      name: "Faridabad City Ride",
      date: "2025-09-10",
      location: "Faridabad, Haryana",
      status: "Completed",
    },
    {
      id: 2,
      name: "Koset 50K Challenge",
      date: "2025-08-15",
      location: "Delhi NCR",
      status: "Completed",
    },
    {
      id: 3,
      name: "Independence Day Route Marathon",
      date: "2025-08-10",
      location: "Gurgaon, Haryana",
      status: "Missed",
    },
    {
      id: 4,
      name: "EcoRide 2025",
      date: "2025-10-20",
      location: "Noida, UP",
      status: "Upcoming",
    },
  ];

  // Status color map
  const statusStyles = {
    Completed: {
      color: "text-green-600 dark:text-green-400",
      bg: "bg-green-100 dark:bg-green-900/30",
      border: "border-green-200 dark:border-green-500/20",
      icon: (
        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
      ),
    },
    Upcoming: {
      color: "text-blue-600 dark:text-blue-400",
      bg: "bg-blue-100 dark:bg-blue-900/30",
      border: "border-blue-200 dark:border-blue-500/20",
      icon: <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
    },
    Missed: {
      color: "text-red-600 dark:text-red-400",
      bg: "bg-red-100 dark:bg-red-900/30",
      border: "border-red-200 dark:border-red-500/20",
      icon: <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />,
    },
  };

  return (
    <section className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Event History
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Track your past and upcoming cycling events
        </p>
      </div>

      {/* Summary Section - Hero Style */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="bg-gradient-to-br from-[#1e8a3b] to-[#114a1f] dark:from-[#166534] dark:to-[#052e16] text-white p-8 rounded-3xl shadow-lg shadow-green-900/20 border border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-green-50 font-medium text-sm uppercase tracking-wider mb-2">
              Total Participation
            </h3>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-white to-green-100 drop-shadow-sm">
                {events.filter((e) => e.status === "Completed").length}
              </span>
              <span className="text-xl text-green-100/80">
                / {events.length} Events
              </span>
            </div>
          </div>
          <div className="hidden sm:block p-4 bg-white/10 rounded-full backdrop-blur-md border border-white/10 shadow-inner">
            <CalendarDays className="w-8 h-8 text-white/90" />
          </div>
        </div>
      </motion.div>

      {/* Event List */}
      <div className="space-y-4">
        {events.map((event, index) => {
          const status = statusStyles[event.status] || statusStyles.Upcoming;
          return (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:shadow-xl hover:scale-[1.01] transition-all duration-300 group">
                {/* Left Section - Event Info */}
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-2xl text-gray-600 dark:text-gray-300 group-hover:bg-green-50 dark:group-hover:bg-green-900/30 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300">
                    <CalendarDays className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-400 transition-colors">
                      {event.name}
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-500 dark:text-gray-400 gap-2 mt-1.5">
                      <span className="flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {new Date(event.date).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                      <span className="hidden sm:inline text-gray-300">•</span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        {event.location}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right Section - Status */}
                <div className="flex items-center self-end sm:self-center">
                  <div
                    className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border ${status.bg} ${status.color} ${status.border} shadow-sm`}
                  >
                    {status.icon}
                    {event.status}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default EventHistory;
